import type { ReactNode } from 'react'
import { Activity, Bell, Boxes, Eraser, HardDriveDownload, Moon, Server, Sun } from 'lucide-react'
import { nodeFresh, useKioskNodes, useKioskSummary } from '@/lib/kiosk'
import { Dot } from '@/components/kiosk/kioskUi'
import { C, type KioskMode } from '@/components/kiosk/kioskTheme'

function Stat({ icon, value, label, color = C.ink }: {
  icon: ReactNode
  value: ReactNode
  label: string
  color?: string
}) {
  return (
    <div className="flex items-center gap-2">
      <span style={{ color: C.dim }}>{icon}</span>
      <span className="kmono text-xl font-bold leading-none" style={{ color }}>{value}</span>
      <span className="text-[11px] uppercase tracking-wide" style={{ color: C.dim }}>{label}</span>
    </div>
  )
}

export function StatusStrip({ now, onAlerts, mode, onToggleTheme, onCare }: {
  now: string
  onAlerts?: () => void
  mode: KioskMode
  onToggleTheme: () => void
  /** Start a screen-cleaning cycle (image persistence). */
  onCare: () => void
}) {
  const summaryQuery = useKioskSummary()
  const nodesQuery = useKioskNodes()
  const s = summaryQuery.data
  const nodes = nodesQuery.data
  const live = !summaryQuery.isError && !nodesQuery.isError &&
    Date.now() - Math.min(summaryQuery.dataUpdatedAt, nodesQuery.dataUpdatedAt) < 90_000
  const liveColor = live ? C.green : C.amber
  // Node count reflects freshness, not the (stale-prone) DiscoveredEntity status.
  const nodesUp = nodes ? nodes.filter(nodeFresh).length : null
  const open = s?.alerts?.open ?? 0
  const crit = s?.alerts?.critical ?? 0
  const alertCol = crit > 0 ? C.red : open > 0 ? C.amber : C.green
  const backupsBad = (s?.backups?.failed_24h ?? 0) > 0

  return (
    <div className="flex h-12 shrink-0 items-center gap-6 border-b px-5"
      style={{ borderColor: C.line, background: C.stripGradient }}>
      <div className="flex items-center gap-2">
        <span className="text-lg font-black tracking-[0.2em]" style={{ color: C.ink }}>ZEUS</span>
        <Dot color={liveColor} pulse={live} />
      </div>
      <div className="mx-1 h-6 w-px" style={{ background: C.line }} />
      <Stat icon={<Server className="h-4 w-4" />} value={nodesUp ?? '—'} label="nodes" color={nodesUp != null && s?.cluster?.nodes != null && nodesUp < s.cluster.nodes ? C.amber : C.ink} />
      <Stat icon={<Boxes className="h-4 w-4" />} value={s?.cluster?.services ?? '—'} label="services" />
      <button
        onClick={onAlerts}
        className="flex items-center gap-2 rounded-lg px-2 py-1 transition-colors active:scale-95"
        style={{ background: open > 0 ? `${alertCol}14` : 'transparent', border: `1px solid ${open > 0 ? `${alertCol}33` : 'transparent'}` }}
      >
        <Bell className="h-4 w-4" style={{ color: open > 0 ? alertCol : C.dim }} />
        <span className={`kmono text-xl font-bold leading-none ${crit > 0 ? 'animate-pulse' : ''}`} style={{ color: alertCol }}>{open}</span>
        <span className="text-[11px] uppercase tracking-wide" style={{ color: C.dim }}>
          {crit > 0 ? `alertes · ${crit} crit` : 'alertes'}
        </span>
      </button>
      <Stat
        icon={<HardDriveDownload className="h-4 w-4" />}
        value={!live ? '—' : backupsBad ? '!' : '✓'}
        label="backups"
        color={!live ? C.amber : backupsBad ? C.red : C.green}
      />
      <div className="ml-auto flex items-center gap-2">
        {/* Touch target sized for a finger on the wall panel, not a cursor. */}
        <button
          onClick={onCare}
          aria-label="Lancer le soin de l'écran"
          className="flex h-9 w-9 items-center justify-center rounded-lg transition-colors active:scale-95"
          style={{ background: C.panelHi, color: C.dim }}
        >
          <Eraser className="h-4 w-4" />
        </button>
        <button
          onClick={onToggleTheme}
          aria-label={mode === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
          className="mr-1 flex h-9 w-9 items-center justify-center rounded-lg transition-colors active:scale-95"
          style={{ background: C.panelHi, color: C.dim }}
        >
          {mode === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        <Activity className="h-4 w-4" style={{ color: liveColor }} />
        <span className="kmono text-sm" style={{ color: C.dim }}>{now}</span>
        <span className="text-[10px] font-bold tracking-widest" style={{ color: liveColor }}>{live ? 'LIVE' : 'DONNÉES ANCIENNES'}</span>
      </div>
    </div>
  )
}
